import type { CSSProperties } from "react";

import type { components } from "../../shared/api/generated/trading-engine.js";
import { formatUsd } from "../positions/models.js";
import type { BacktestPnlGroupBy } from "../bots/backtest-queries.js";
import { pnlPeriodLabel } from "./pnl-chart-models.js";

type ChartItem = components["schemas"]["BacktestPnlChart"]["items"][number];

export function BacktestPnlChart({
  groupBy,
  items,
  onSelect,
}: {
  readonly groupBy: BacktestPnlGroupBy;
  readonly items: readonly ChartItem[];
  readonly onSelect: (item: ChartItem) => void;
}) {
  const maximum = Math.max(1, ...items.map((item) => Math.abs(Number(item.realizedPnlUsd))));
  return (
    <div className="backtest-pnl-chart" aria-label={`Realized P/L by ${groupBy}`}>
      <div className="backtest-pnl-chart__scroll">
        <div
          className="backtest-pnl-chart__columns"
          style={{ "--chart-column-count": Math.max(1, items.length) } as CSSProperties}
        >
          {items.map((item) => {
            const value = Number(item.realizedPnlUsd);
            const percentage = Math.max(1, (Math.abs(value) / maximum) * 44);
            const label = pnlPeriodLabel(groupBy, item.key);
            return (
              <button
                aria-label={`${label}: ${formatUsd(item.realizedPnlUsd)} across ${String(item.tradeCount)} closing trades. ${groupBy === "symbol" ? "Open ticker chart." : "Filter to this period."}`}
                className="backtest-pnl-chart__column"
                key={item.key}
                onClick={() => onSelect(item)}
                title={`${label} · ${formatUsd(item.realizedPnlUsd)} · ${String(item.tradeCount)} trades · ${String(item.runCount)} runs`}
                type="button"
              >
                <span className="backtest-pnl-chart__plot" aria-hidden="true">
                  <span className="backtest-pnl-chart__zero" />
                  <span
                    className={`backtest-pnl-chart__bar backtest-pnl-chart__bar--${value < 0 ? "loss" : "gain"}`}
                    style={
                      {
                        "--bar-size": `${percentage}%`,
                      } as CSSProperties
                    }
                  />
                  <strong>{formatUsd(item.realizedPnlUsd)}</strong>
                </span>
                <span className="backtest-pnl-chart__label">{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
